import React, { useState, useEffect } from "react";
import {
  PlayIcon,
  DocumentIcon,
  CircleStackIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FolderOpenIcon,
  ArrowPathIcon,
  EyeIcon,
  TrashIcon,
  XMarkIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import {
  MetadataFile,
  DataFile,
  SelectOption,
  JobResult,
  DynamicTableData,
} from "../../types";
import ApiService from "../../services/api";
import Select from "react-select";
import { useProject } from "../../contexts/ProjectContext";
import DAPTable from "../DAPTable/DAPTable";

interface PushJobSectionProps {
  isActive: boolean;
}

const PushJobSection: React.FC<PushJobSectionProps> = ({ isActive }) => {
  const { selectedProject } = useProject();
  const [metadataFiles, setMetadataFiles] = useState<MetadataFile[]>([]);
  const [dataFiles, setDataFiles] = useState<DataFile[]>([]);
  const [selectedMetadata, setSelectedMetadata] = useState<SelectOption | null>(null);
  const [selectedData, setSelectedData] = useState<SelectOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [jobResult, setJobResult] = useState<JobResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentJobs, setRecentJobs] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isTableExists, setIsTableExists] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableData, setTableData] = useState<any>(null);
  const [tableError, setTableError] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<'load' | 'review' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (!isActive) return;
    if (!selectedProject?.id) return;
    loadFiles();
    loadRecentJobs();
  }, [isActive, selectedProject?.id]);

  // When project changes, reload project-scoped data and reset selections
  useEffect(() => {
    if (!isActive) return;
    // Clear current selections/state to avoid mixing across projects
    setSelectedData(null);
    setSelectedMetadata(null);
    setJobResult(null);
    // Refetch lists for the newly selected project
    loadFiles();
    loadRecentJobs();
  }, [selectedProject?.id, isActive]);

  useEffect(() => {
    if (selectedData && isActive) {
      checkTableExists();
    }
  }, [selectedData, isActive]);

  const loadFiles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const pid = selectedProject?.id;
      const [metadataResponse, dataResponse] = await Promise.all([
        ApiService.getMetadataFiles(0, 100, pid),
        ApiService.getDataFiles(0, 100, pid),
      ]);

      setMetadataFiles(metadataResponse.data);
      setDataFiles(dataResponse.data);
    } catch (err) {
      setError("Failed to load files. Please check if the API is running.");
      console.error("Error loading files:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentJobs = async () => {
    try {
      const pid = selectedProject?.id;
      const response = await ApiService.getJobs(undefined, undefined, 0, 5, true, pid);
      setRecentJobs(response.data || []);
    } catch (err) {
      console.error("Error loading recent Workflows:", err);
    }
  };

  const handleSubmitJob = async () => {
    if (!selectedData || !selectedMetadata) {
      setError("Please select both data file and metadata file.");
      return;
    }

    // Check if table exists for the selected data file
    const tableExists = await checkTableExists();
    if (!tableExists) {
      setError("Please load the data into database first using the Load button.");
      return;
    }

    setIsCreatingJob(true);
    setError(null);

    try {
      const response = await ApiService.createJob({
        data_file_id: selectedData.value,
        metadata_file_id: selectedMetadata.value,
        started_by: "admin",
      });

      setJobResult({
        success: true,
        job_id: response.job_id,
        message: response.message
      });
      
      
      // Refresh recent jobs
      loadRecentJobs();
      
    } catch (err) {
      console.error("Error creating Workflow:", err);
      setError("Failed to create Workflow. Please try again.");
    } finally {
      setIsCreatingJob(false);
    }
  };


  const getJobStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'FAILED':
        return <XCircleIcon className="w-4 h-4 text-red-600" />;
      case 'STARTED':
      case 'INPROGRESS':
        return <ClockIcon className="w-4 h-4 text-blue-600" />;
      case 'WAITING_FOR_APPROVAL':
        return <ExclamationTriangleIcon className="w-4 h-4 text-orange-600" />;
      default:
        return <ClockIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'FAILED':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'STARTED':
      case 'INPROGRESS':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'WAITING_FOR_APPROVAL':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const metadataOptions: SelectOption[] = metadataFiles.map((file) => ({
    value: file.id,
    label: file.file_name+'(ID:'+file.id+')',
  }));

  const dataOptions: SelectOption[] = dataFiles.map((file) => ({
    value: file.id,
    label: file.file_name+'(ID:'+file.id+')',
  }));

  // Function to check if table exists for selected data file
  const checkTableExists = async () => {
    if (!selectedData) return;
    
    try {
      // Find the selected data file
      const dataFile = dataFiles.find(file => file.id === Number(selectedData.value));
      if (!dataFile) return;
      
      // Check if table exists for this file
      setIsLoadingData(true);
      const exists = await ApiService.checkTableExistsForDataFile(dataFile.file_name);
      setIsTableExists(exists);
      
      return exists;
    } catch (error) {
      console.error("Error checking if table exists:", error);
      return false;
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Function to load data into database
  const loadDataIntoDatabase = async () => {
    if (!selectedData) return;
    
    try {
      setIsLoadingData(true);
      setTableError(null);
      
      // Find the selected data file
      const dataFile = dataFiles.find(file => file.id === Number(selectedData.value));
      if (!dataFile) {
        setTableError("Selected data file not found");
        return;
      }
      
      // Get table name from data file name (remove extension and sanitize)
      const tableName = dataFile.file_name.replace(/\.[^/.]+$/, "")
      
      console.log(`Creating table '${tableName}' for data file ID ${dataFile.id}`);
      
      // Get metadata file ID using data_metadata_mapper
      let metadataFileId = null;
      let columns: Record<string, string> = {};
      
      metadataFileId = await ApiService.getMetadataFileIdByDataFileId(dataFile.id);
      
      if (metadataFileId) {
        console.log(`Found metadata file ID ${metadataFileId} from data_metadata_mapper`);
        
        // Get metadata content using the metadata file ID
        const metadataContent = await ApiService.viewMetadataFile(metadataFileId);
        console.log("Metadata content:", metadataContent);
        
        // Create columns from metadata content
        if (metadataContent && metadataContent.preview && Array.isArray(metadataContent.preview)) {
          metadataContent.preview.forEach((field: any) => {
            if (field.column_name && field.data_type) {
              columns[field.column_name] = field.data_type.toLowerCase();
            }
          });
        }
      } else {
        console.log("No metadata found for this data file.");
      }
      
      // If no metadata found, use default columns
      if (Object.keys(columns).length === 0) {
        console.log("Using default columns as no metadata was found.");
        columns = {
          id: "integer",
          name: "string",
          description: "text",
          value: "float",
          created_date: "datetime"
        };
      }
      
      // Map unsupported data types to supported ones
      const mappedColumns: Record<string, string> = {};
      for (const [columnName, dataType] of Object.entries(columns)) {
        // Map 'categorical' to 'varchar' (string)
        if (dataType === 'categorical') {
          mappedColumns[columnName] = 'varchar';
        } else {
          mappedColumns[columnName] = dataType;
        }
      }
      
      console.log("Creating table with columns:", mappedColumns);
      
      // Create the table first
      let tableCreated = false;
      try {

        const tableResult = await ApiService.createDynamicTable(tableName, mappedColumns);
        console.log("Table creation result:", tableResult);
        tableCreated = true;
      } catch (error: any) {
        // Check if the error is because the table already exists
        if (error.message && error.message.includes("Table already exists")) {
          console.log("Table already exists, proceeding with loading data");
          tableCreated = true;
        } else {
          // For other errors, throw the error to be caught by the outer try-catch
          console.error("Failed to create table:", error);
          throw error;
        }
      }
      
      // Only proceed with loading data if the table was created or already exists
      if (tableCreated) {
        // Then load the data into the table with the sanitized table name
        const result = await ApiService.loadCsvData(dataFile.id, tableName);
        
        if (result.already_exists) {
          setIsTableExists(true);
        } else {
          // Table was created successfully
          setIsTableExists(true);
        }
        
        return result;
      }
    } catch (error) {
      console.error("Error loading data into database:", error);
      setTableError("Failed to load data into database. Please try again.");
      return null;
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Function to fetch and display table data
  const fetchTableData = async (page: number = currentPage) => {
    if (!selectedData) return;
    
    try {
      setIsLoadingData(true);
      setTableError(null);
      
      // Find the selected data file
      const dataFile = dataFiles.find(file => file.id === Number(selectedData.value));
      if (!dataFile) {
        setTableError("Selected data file not found");
        return;
      }
      
      // Get table name (file name without extension)
      const tableName = dataFile.file_name.replace(/\.[^/.]+$/, "");
      console.info("Rows per page selected is: ", pageSize);
      // Fetch table data with pagination
      const data = await ApiService.getDynamicTableData(tableName, page, pageSize);
      setTableData(data);
      setCurrentPage(page);
      setShowTableModal(true);
      
    } catch (error) {
      console.error("Error fetching table data:", error);
      setTableError("Failed to fetch table data. Please try again.");
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Function to handle Load/Review button click
  const handleLoadReviewClick = async () => {
    if (!selectedData) return;
    
    if (isTableExists) {
      // If table exists, fetch and display data (Review action)
      setLoadingAction('review');
      await fetchTableData();
      setLoadingAction(null);
    } else {
      // If table doesn't exist, load data into database (Load action)
      setLoadingAction('load');

      const result = await loadDataIntoDatabase();
      if (result && !result.error) {
        // If data was loaded successfully, update the button state
        setIsTableExists(true);
      }
      setLoadingAction(null);
    }
  };
  
  // Function to close the table modal
  const closeTableModal = () => {
    setShowTableModal(false);
    setTableData(null);
    setTableError(null);
    setCurrentPage(1); // Reset to first page when closing
  };

  // Function to handle page navigation
  const handlePageChange = (newPage: number) => {
    if (!tableData) return;
    
    const totalPages = calculateTotalPages(tableData.total_rows, pageSize);
    if (newPage < 1 || newPage > totalPages) return;
    
    fetchTableData(newPage);
  };

  // Function to go to the next page
  const goToNextPage = () => {
    if (tableData) {
      const totalPages = calculateTotalPages(tableData.total_rows, pageSize);
      if (currentPage < totalPages) {
        handlePageChange(currentPage + 1);
      }
    }
  };

  // Function to go to the previous page
  const goToPrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Function to go to the first page
  const goToFirstPage = () => {
    handlePageChange(1);
  };

  // Function to go to the last page
  const goToLastPage = () => {
    if (tableData) {
      const totalPages = calculateTotalPages(tableData.total_rows, pageSize);
      if (totalPages > 0) {
        handlePageChange(totalPages);
      }
    }
  };

  // Function to calculate total pages
  const calculateTotalPages = (totalRows: number, pageSize: number): number => {
    return Math.ceil(totalRows / pageSize);
  };

  const renderTableModal = () => {
    if (!showTableModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900">
              {tableData ? `Table Data: ${tableData.table_name}` : 'Table Data'}
            </h3>
            <button 
              onClick={closeTableModal}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="flex-1 p-6 overflow-auto">
            {isLoadingData && (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            )}

            {tableError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <p>{tableError}</p>
              </div>
            )}

            {!isLoadingData && !tableError && tableData && (
              <div>
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <span className="text-sm text-gray-500 mr-2">Total Rows:</span>
                    <span className="text-sm font-medium">{tableData.total_rows}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 mr-2">Page:</span>
                    <span className="text-sm font-medium">{currentPage} of {calculateTotalPages(tableData.total_rows, pageSize)}</span>
                  </div>
                </div>

                <DAPTable
                  columns={tableData.columns.map((col: string) => ({
                    key: col,
                    label: col.charAt(0).toUpperCase() + col.slice(1).replace('_', ' '),
                    align: 'left'
                  }))}
                  data={tableData.data}
                  title={`${tableData.table_name} Data`}
                  emptyMessage="No data available"
                  striped={true}
                  hoverable={true} showValidateButton={false}
                />
                
                {/* Pagination Controls */}
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500">Rows per page:</span>
                    <select
                      className="ml-2 border border-gray-300 rounded px-2 py-1 text-sm"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                        fetchTableData(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={goToFirstPage}
                      disabled={currentPage === 1 || isLoadingData}
                      className={`p-1 rounded ${
                        currentPage === 1 || isLoadingData
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      title="First Page"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={goToPrevPage}
                      disabled={currentPage === 1 || isLoadingData}
                      className={`p-1 rounded ${
                        currentPage === 1 || isLoadingData
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      title="Previous Page"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    <span className="text-sm text-gray-700">
                      Page <span className="font-medium">{currentPage}</span> of{" "}
                      <span className="font-medium">{calculateTotalPages(tableData.total_rows, pageSize)}</span>
                    </span>
                    
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === calculateTotalPages(tableData.total_rows, pageSize) || isLoadingData}
                      className={`p-1 rounded ${
                        currentPage === calculateTotalPages(tableData.total_rows, pageSize) || isLoadingData
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      title="Next Page"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={goToLastPage}
                      disabled={currentPage === calculateTotalPages(tableData.total_rows, pageSize) || isLoadingData}
                      className={`p-1 rounded ${
                        currentPage === calculateTotalPages(tableData.total_rows, pageSize) || isLoadingData
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      title="Last Page"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 6.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={closeTableModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-3">
      {/* Table Data Modal */}
      {renderTableModal()}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-secondary-900 mb-1">Create Workflow</h1>
        <p className="text-secondary-600 text-sm">
          Create data processing workflows for validation, profiling, and analysis
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          <div className="flex items-center">
            <XCircleIcon className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {jobResult && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
          <div className="flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <div>
              <p className="text-green-800 text-sm font-medium">
                Workflow created successfully!
              </p>
              <p className="text-green-700 text-xs mt-1">
                Workflow ID: {jobResult.job_id}
              </p>
            </div>
          </div>
        </div>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Job Submission Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-4 mb-4">
            <h2 className="text-lg font-semibold text-secondary-900 mb-3 flex items-center">
              <PlayIcon className="w-6 h-6 text-primary-600 mr-2" />
              Create Workflow
            </h2>

            <div className="space-y-3">
              {/* Data File Selection */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Select Data File
                </label>
                <div className="flex space-x-2">
                  <div className="flex-grow">
                    <Select
                      value={selectedData}
                      onChange={setSelectedData}
                      options={dataOptions}
                      placeholder="Choose a data file..."
                      className="text-sm"
                      isDisabled={isLoading}
                    />
                  </div>
                  <button
                    onClick={handleLoadReviewClick}
                    disabled={!selectedData || isLoadingData}
                    className={`px-4 py-2 text-sm rounded-lg font-medium transition-all duration-200 ${
                      !selectedData || isLoadingData
                        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                        : isTableExists
                        ? "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md"
                        : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md"
                    }`}
                  >
                    {isLoadingData ? (
                      <div className="flex items-center justify-center">
                        <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                        {loadingAction === 'review' ? "Fetching..." : "Loading..."}
                      </div>
                    ) : isTableExists ? (
                      <div className="flex items-center justify-center">
                        <EyeIcon className="w-4 h-4 mr-2" />
                        Review
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <TableCellsIcon className="w-4 h-4 mr-2" />
                        Load
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* Metadata File Selection */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Select Metadata File
                </label>
                <Select
                  value={selectedMetadata}
                  onChange={setSelectedMetadata}
                  options={metadataOptions}
                  placeholder="Choose a metadata file..."
                  className="text-sm"
                  isDisabled={isLoading}
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  onClick={handleSubmitJob}
                  disabled={isCreatingJob || !selectedData || !selectedMetadata || !isTableExists}
                  className={`w-full px-4 py-3 text-white text-sm rounded-lg font-medium transition-all duration-200 ${
                    isCreatingJob || !selectedData || !selectedMetadata || !isTableExists
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary-600 hover:bg-primary-700 shadow-md hover:shadow-lg"
                  }`}
                >
                  {isCreatingJob ? (
                    <div className="flex items-center justify-center">
                      <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                      Creating Workflow...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <PlayIcon className="w-4 h-4 mr-2" />
                      Create Workflow
                    </div>
                  )}
                </button>
                {!isTableExists && selectedData && (
                  <p className="text-amber-600 text-xs mt-2">
                    <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
                    Please load the data using the Load button before creating a workflow.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Available Files Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <FolderOpenIcon className="w-5 h-5 text-primary-600 mr-2" />
                Available Files
              </h3>
              <button
                onClick={loadFiles}
                disabled={isLoading}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
              >
                <ArrowPathIcon className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Data Files */}
              <div>
                <h4 className="text-sm font-medium text-secondary-700 mb-2">
                  Data Files ({dataFiles.length})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dataFiles.map((file) => (
                    <div
                      key={file.id}
                      className="p-2 bg-secondary-50 rounded border text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-secondary-900 truncate">
                          {file.file_name}
                        </span>
                        <div className="flex items-center space-x-1">
                          <button
                            className="text-secondary-500 hover:text-secondary-700"
                            title="View file details"
                          >
                            <EyeIcon className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <p className="text-secondary-500 mt-1">
                        Created: {new Date(file.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                  {dataFiles.length === 0 && (
                    <p className="text-secondary-500 text-center py-4">
                      No data files available
                    </p>
                  )}
                </div>
              </div>

              {/* Metadata Files */}
              <div>
                <h4 className="text-sm font-medium text-secondary-700 mb-2">
                  Metadata Files ({metadataFiles.length})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {metadataFiles.map((file) => (
                    <div
                      key={file.id}
                      className="p-2 bg-secondary-50 rounded border text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-secondary-900 truncate">
                          {file.file_name}
                        </span>
                        <div className="flex items-center space-x-1">
                          <button
                            className="text-secondary-500 hover:text-secondary-700"
                            title="View file details"
                          >
                            <EyeIcon className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <p className="text-secondary-500 mt-1">
                        Created: {new Date(file.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                  {metadataFiles.length === 0 && (
                    <p className="text-secondary-500 text-center py-4">
                      No metadata files available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

                  {/* Right Sidebar */}
                  <div className="space-y-6">
          {/* Job Status Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 h-[318px] flex flex-col">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
              <CircleStackIcon className="w-5 h-5 text-primary-600 mr-2" />
              Workflow Status
            </h2>

            <div className="flex-1 flex items-center justify-center">
              {jobResult ? (
                <div
                  className={`p-4 rounded-lg border w-full ${
                    jobResult.success
                      ? "bg-green-50 border-green-200"
                      : "bg-red-50 border-red-200"
                  }`}
                >
                  <div className="flex items-start">
                    {jobResult.success ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h3
                        className={`font-medium text-sm ${
                          jobResult.success
                            ? "text-green-800"
                            : "text-red-800"
                        }`}
                      >
                        {jobResult.success
                          ? "Workflow Created Successfully"
                          : "Workflow Creation Failed"}
                      </h3>
                      <p
                        className={`text-xs mt-1 ${
                          jobResult.success
                            ? "text-green-700"
                            : "text-red-700"
                        }`}
                      >
                        {jobResult.message}
                      </p>
                      {jobResult.success && jobResult.job_id && (
                        <div className="mt-2 p-2 bg-white rounded border">
                          <p className="text-xs font-medium text-secondary-700">
                            Workflow ID:
                          </p>
                          <p className="text-sm font-mono text-primary-600">
                            {jobResult.job_id}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <CircleStackIcon className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
                  <p className="text-secondary-500 text-sm">
                    No workflow created yet
                  </p>
                  <p className="text-secondary-400 text-xs mt-1">
                    Create a workflow to see status here
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 h-[300px] flex flex-col">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
              <CircleStackIcon className="w-5 h-5 text-primary-600 mr-2" />
              Quick Actions
            </h3>

            <div className="flex-1 flex flex-col justify-center space-y-3">
              <button
                onClick={loadFiles}
                disabled={isLoading}
                className="w-full text-left p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
              >
                <div className="flex items-center">
                  <ArrowPathIcon className={`w-4 h-4 text-primary-600 mr-3 ${isLoading ? 'animate-spin' : ''}`} />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">
                      Refresh Files
                    </p>
                    <p className="text-xs text-secondary-600">
                      Reload available files
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={loadRecentJobs}
                className="w-full text-left p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
              >
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 text-primary-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">
                      Refresh Workflows
                    </p>
                    <p className="text-xs text-secondary-600">
                      Update workflow status
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setSelectedData(null);
                  setSelectedMetadata(null);
                  setJobResult(null);
                  setError(null);
                }}
                className="w-full text-left p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
              >
                <div className="flex items-center">
                  <TrashIcon className="w-4 h-4 text-secondary-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">
                      Clear Form
                    </p>
                    <p className="text-xs text-secondary-600">
                      Reset all selections
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
        

        <div className="lg:col-span-3">
          {/* Recent Jobs Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
              <ClockIcon className="w-5 h-5 text-primary-600 mr-1" />
              Recent Workflows
            </h3>

            <div className="space-y-3">
              {recentJobs.slice(0, 5).map((job) => (
                <div
                  key={job.id}
                  className="p-3 border border-secondary-200 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-secondary-900">
                      Workflow #{job.id}
                    </span>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getJobStatusColor(job.status)}`}>
                      <div className="flex items-center">
                        {getJobStatusIcon(job.status)}
                        <span className="ml-1">{job.status}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-secondary-600">
                    <p>Data: {job.data_file_name || 'N/A'}</p>
                    <p>Metadata: {job.metadata_file_name || 'N/A'}</p>
                    <p>Started: {new Date(job.started_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              {recentJobs.length === 0 && (
                <p className="text-secondary-500 text-center py-4 text-sm">
                  No recent workflows
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushJobSection;
